import React, { ReactNode } from 'react'
import Head from 'next/head'
import { makeStyles } from 'tss-react/mui'
import { productDetailRoute, useRouter } from '@src/routes'
import { Link } from '@src/components/Link'
import {
  CarouselProduct,
  CarouselProductTypes,
} from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import { NewPdpProductLockupMini } from './NewPdpProductLockupMini'
import { useCategorizePage } from '@src/utils/analytics/CategorizePage'
import { sendCarouselItemClickEvent } from '@src/utils/analytics/carouselAnalytics'

const useStyles = makeStyles()((theme) => ({
  link: {
    border: '2px solid transparent',
    display: 'block',
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.text.primary,
    // match CTA button focus styles
    '&:focus-visible': {
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: theme.shape.borderRadius,
      boxShadow: `0 0 6px 0 ${theme.palette.primary.main}`,
      outline: 'none',
    },
  },
  footer: {
    marginTop: theme.spacing(6),
  },
  footerRaisedCard: {
    marginTop: theme.spacing(4),
  },
  btn: {
    whiteSpace: 'nowrap',
  },
}))

interface NewPdpDynamicProductCardProps {
  product: CarouselProduct
  testId: string
  index: number
  type: CarouselProductTypes
  onLinkClick?: () => void
  preloadCardImages?: boolean
  className?: string
  hideProductImg?: boolean
  displayDiscount?: (discountPercent?: string | null) => ReactNode
}

const NewPdpDynamicProductCard: React.FC<NewPdpDynamicProductCardProps> = ({
  product,
  testId,
  index,
  type,
  onLinkClick,
  preloadCardImages = false,
  className,
  hideProductImg = false,
  displayDiscount,
}) => {
  const router = useRouter()
  const catalog = router?.query?.catalog as string | (string[] & string)
  const { classes, cx } = useStyles()
  const linkId = `product-card-${product.productNumber}${product.brand.key}-${testId}`

  const pageType = useCategorizePage()
  const analyticsCreativeSlot = `${type} - ${pageType}`

  return (
    <div className={cx(className, 'promotions-slider-div')}>
      {preloadCardImages && (
        <Head>
          <link rel="preload" href={product.images[0].mediumUrl} as="image" />
        </Head>
      )}
      <Link
        {...productDetailRoute.index(product.brand.key, product.productKey, {
          catalog,
        })}
        passHref
      >
        {/* jsx-a11y rules disabled because href is implicitly passed to anchor via Link component */}
        <a
          className={classes.link}
          data-testid={linkId}
          id={linkId}
          onClick={() => {
            if (onLinkClick) onLinkClick()
            sendCarouselItemClickEvent({
              product,
              indexPosition: index,
              creativeSlot: analyticsCreativeSlot,
              carouselType: type,
            })
          }}
        >
          <NewPdpProductLockupMini
            productNumber={product.productNumber}
            name={product.name}
            description={product.description}
            image={!hideProductImg ? product.images[0] : undefined}
            displayDiscount={displayDiscount}
            discount={product.discount}
          />
        </a>
      </Link>
    </div>
  )
}

export default NewPdpDynamicProductCard
